import {Feature} from 'geojson';
import {PlotData} from 'plotly.js';
import {FrontOfficeEventStatus} from '../constants';
import {GeoJsonPropertiesWazeIncident} from './GeoJsonSources/GeoJsonPropertiesWaze';
import {MapFeature} from './MapFeature';
import {WAZE_ALERT_ITEM_SUB_TYPES} from './WazeAlertItemStatus';

export enum DialogTypes {
    SELECT_FEATURES = 'selectFeatures',
    GEO_JSON = 'geoJSON',
    VECTOR_LAYER = 'vectorLayer',
}

export type DIALOG_TYPES = DialogTypes.SELECT_FEATURES | DialogTypes.GEO_JSON | DialogTypes.VECTOR_LAYER;
export type MAP_LAYER = 'vms-msi' | 'bridge' | 'wazeIncidents' | 'drips' | 'vehicleObstructionEmergencyVehicle' | 'here_all'
    | 'here' | 'schoolzones' | 'trafficMessageOnda'| 'trafficMessageFlisvis' | 'trafficMessageOtis' | 'roadWorkActual' | 'roadWorkPlanned'
    | 'roadWorkPlannedOtm' | 'fd' | 'wazeOtherTrafficJams' | 'fcdClass' | 'wazeOtherIrregularity' | 'wazeOtherTrafficAlerts'
    | 'traveltime-fcd' | 'traveltime-drips' | 'traveltime-other' | 'travelTimeFcd' | 'travelTimeDrips' | 'travelTimeOther'
    | 'rushHourLane' | 'msi' | 'dfine' | 'avg' | 'im' | 'wazeAlertOther' | 'safetyRelatedInformation';

export enum SavingStatus {
    callingServer = 'CALLING_SERVER',
    changesSaved = 'CHANGES_SAVED',
    savingFailed = 'SAVING_FAILED',
}

export enum GeoJsonSubTypes {
    GENERAL = 'general',
    BRIDGE = 'bridge',
    WAZE_ALERT_INCIDENT = 'wazeAlertIncident',
}

export type GEO_JSON_SUB_TYPES =
    | GeoJsonSubTypes.GENERAL
    | GeoJsonSubTypes.BRIDGE
    | GeoJsonSubTypes.WAZE_ALERT_INCIDENT;

export enum VectorLayerSubTypes {
    GENERAL = 'general',
    TRAVEL_TIME_FCD_V2 = 'traveltime-fcd',
    TRAVEL_TIME_DRIPS_V2 = 'traveltime-drips',
    TRAVEL_TIME_OTHER_V2 = 'traveltime-other',
    AVG = 'avg',
}

export interface ActiveBaseDialog {
    type: DIALOG_TYPES;
    id: number | string;
    pinned: boolean;
    preview: boolean;
    startPosition: {
        x: number;
        y: number;
    };
    zIndex: number;
}

export interface ActiveDialogSelectFeature extends ActiveBaseDialog {
    type: DialogTypes.SELECT_FEATURES;
    features: MapFeature[];
}

export interface ActiveDialogGeoJsonFeature<P = Feature['properties']> extends ActiveBaseDialog {
    type: DialogTypes.GEO_JSON;
    subType: GEO_JSON_SUB_TYPES;
    currentProperties: P;
    mapLayer: MAP_LAYER;
    dataSource: string;
}

export interface ActiveDialogGeoJsonFeatureGeneral extends ActiveDialogGeoJsonFeature {
    subType: GeoJsonSubTypes.GENERAL;
}

export interface ActiveDialogAvgGraphData {
    flow: Partial<PlotData>[];
    speed: Partial<PlotData>[];
    timeStamp: number;
}

export interface ActiveDialogAvg extends ActiveDialogVectorLayerFeature {
    subType: VectorLayerSubTypes.AVG;
    graphs: ActiveDialogAvgGraphData | null;
}

export interface ActiveDialogGeoJsonFeatureBridge extends ActiveDialogGeoJsonFeature {
    subType: GeoJsonSubTypes.BRIDGE;
    bridgeHistory: Record<string, number> | null;
}

export interface ActiveDialogGeoJsonFeatureWazeAlertIncident extends ActiveDialogGeoJsonFeature {
    subType: GeoJsonSubTypes.WAZE_ALERT_INCIDENT;
    currentProperties: GeoJsonPropertiesWazeIncident;
    lastChangeTimestamp: number | null;
    lastSaveTimestamp: number | null;
    isLocked: boolean;
    status: FrontOfficeEventStatus | null;
    timeRemainingBeforeAutomaticClosure: number | null;
    overriddenRoadInformation: string | null;
    savingStatus: SavingStatus | null;
    overriddenType: WAZE_ALERT_ITEM_SUB_TYPES | null;
}

export interface ActiveDialogVectorLayerFeature extends ActiveBaseDialog {
    type: DialogTypes.VECTOR_LAYER;
    subType: VectorLayerSubTypes;
    currentProperties: Feature['properties'];
    currentState: Record<string, unknown>;
    mapLayer: MAP_LAYER;
    dataSource: string;
}

export interface ActiveDialogVectorLayerFeatureGeneral extends ActiveDialogVectorLayerFeature {
    subType: VectorLayerSubTypes.GENERAL;
}

export interface ActiveDialogVectorLayerFeatureTravelTimeGraphData {
    travelTime: Partial<PlotData>[];
    velocity: Partial<PlotData>[];
    timeStamp: number;
}

export interface ActiveDialogVectorLayerFeatureTravelTimeV2GraphData {
    travelTime: Partial<PlotData>[];
    speed: Partial<PlotData>[];
    timeStamp: number;
}

export interface ActiveDialogVectorLayerFeatureTravelTimeV2 extends ActiveDialogVectorLayerFeature {
    subType:
        | VectorLayerSubTypes.TRAVEL_TIME_FCD_V2
        | VectorLayerSubTypes.TRAVEL_TIME_OTHER_V2
        | VectorLayerSubTypes.TRAVEL_TIME_DRIPS_V2;
    graphs: ActiveDialogVectorLayerFeatureTravelTimeV2GraphData | null;
}


export type DIALOG_INTERFACES =
    | ActiveDialogSelectFeature
    | ActiveDialogGeoJsonFeatureGeneral
    | ActiveDialogGeoJsonFeatureBridge
    | ActiveDialogGeoJsonFeatureWazeAlertIncident
    | ActiveDialogAvg
    | ActiveDialogVectorLayerFeatureGeneral
    | ActiveDialogVectorLayerFeatureTravelTimeV2;

export type DIALOG_INTERFACES_GEO_JSON =
    | ActiveDialogGeoJsonFeatureGeneral
    | ActiveDialogGeoJsonFeatureBridge
    | ActiveDialogGeoJsonFeatureWazeAlertIncident;

export type DIALOG_INTERFACES_VECTOR_LAYER =
    | ActiveDialogVectorLayerFeatureGeneral
    | ActiveDialogAvg
    | ActiveDialogVectorLayerFeatureTravelTimeV2;
